import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { WelcomeScreen } from './pages/WelcomeScreen';
import { FamilyCreation } from './pages/FamilyCreation';
import { JoinExistingFamily } from './pages/JoinExistingFamily';
import { KidLogin } from './pages/KidLogin';
import { FamilyCreationSuccess } from './pages/FamilyCreationSuccess';
import { FamilyDashboard } from './pages/FamilyDashboard';
import { GuardianLogin } from './pages/GuardianLogin';
import { AddChild } from './pages/AddChild';
import { ChildDashboard } from './pages/ChildDashboard';
import { AddMoney } from './pages/AddMoney';
import { LoginHandler } from './utils/LoginHandler';
import { supabase } from './utils/supabaseClient';
import FinancialLiteracyLessonRenderer from './chapters/FinancialLiteracyLessonRenderer';
import { OnboardingFamilyCreation } from './pages/OnboardingFamilyCreation';
import AdminDashboard from './pages/AdminDashboard';
import LedgerBookPage from './pages/LedgerBookPage';
import Chapter from './chapters/Chapter';

// Financial Literacy Program imports
import FinancialLiteracyProgram from './pages/FinancialLiteracyProgram';

// Create a context for app state to share across routes
const AppContext = React.createContext();

// Main App wrapper with Router
const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

// App content with access to router hooks
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentUser, setCurrentUser] = useState(null);
  const [currentFamily, setCurrentFamily] = useState(null);
  const [currentGuardian, setCurrentGuardian] = useState(null);
  const [children, setChildren] = useState([]);
  const [guardians, setGuardians] = useState([]);
  const [loginData, setLoginData] = useState(null);
  const [currentChild, setCurrentChild] = useState(null);
  const [userType, setUserType] = useState(null);
  const [inviteCode, setInviteCode] = useState('');
  const [parentRefreshCallback, setParentRefreshCallback] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const inviteProcessedRef = useRef(false);

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app';

  // Check for special URL patterns on initial load
  useEffect(() => {
    if (inviteProcessedRef.current) {
      return;
    }

    const path = location.pathname;
    const urlParams = new URLSearchParams(location.search);
    console.log('Current path on load:', path);
    console.log('URL search params:', urlParams.toString());

    // Check for invite code in PATH
    const inviteMatch = path.match(/\/invite\/([A-Z0-9]{6})/i);
    if (inviteMatch) {
      const code = inviteMatch[1].toUpperCase();
      console.log('DEBUG: Found invite code in path:', code);

      inviteProcessedRef.current = true;
      localStorage.removeItem('authData');
      localStorage.removeItem('userType');
      setInviteCode(code);
      setIsAuthLoading(false);
      navigate('/join-family', { replace: true });
      return;
    }

    // Check for onboarding params
    const onboardingEmail = urlParams.get('email');
    const onboardingCode = urlParams.get('code');

    if (onboardingEmail && onboardingCode) {
      console.log('DEBUG: Found onboarding email and code:', onboardingEmail, onboardingCode);
      inviteProcessedRef.current = true;
      localStorage.removeItem('authData');
      localStorage.removeItem('userType');
      localStorage.setItem('onboardingEmail', onboardingEmail);
      localStorage.setItem('onboardingCode', onboardingCode);
      setIsAuthLoading(false);
      navigate('/onboarding', { replace: true });
      return;
    }

    // Check for invite code in URL PARAMS
    const inviteFromUrl = urlParams.get('invite');
    if (inviteFromUrl && /^[A-Z0-9]{6}$/i.test(inviteFromUrl)) {
      const code = inviteFromUrl.toUpperCase();
      console.log('DEBUG: Found invite code in URL params:', code);
      inviteProcessedRef.current = true;
      localStorage.removeItem('authData');
      localStorage.removeItem('userType');
      setInviteCode(code);
      setIsAuthLoading(false);
      navigate('/join-family', { replace: true });
      return;
    }

    // Check for errors
    const errorFromUrl = urlParams.get('error');
    if (errorFromUrl) {
      if (errorFromUrl === 'invalid_invite') {
        alert('This invite link is invalid or has expired.');
      } else if (errorFromUrl === 'server_error') {
        alert('There was a server error processing the invite.');
      }
      navigate('/', { replace: true });
    }

    // Check existing auth
    checkExistingAuth();
  }, []);

  const checkExistingAuth = async () => {
    try {
      const savedAuthData = localStorage.getItem('authData');
      const savedUserType = localStorage.getItem('userType');

      console.log('App: Checking existing auth...');

      if (savedAuthData && savedUserType) {
        try {
          const authData = JSON.parse(savedAuthData);
          console.log('Found existing auth, restoring session:', savedUserType);

          setUserType(savedUserType);
          setCurrentUser(authData.user);

          if (savedUserType === 'admin') {
            navigate('/admin', { replace: true });
          } else if (savedUserType === 'child') {
            setCurrentChild(authData.child);
            setCurrentFamily(authData.family);
            navigate('/child-dashboard', { replace: true });
          } else if (savedUserType === 'guardian') {
            setCurrentGuardian(authData.guardian);
            setCurrentFamily(authData.family);
            setGuardians(authData.guardians || []);

            // Fetch fresh children data
            if (authData.family?.id) {
              try {
                const response = await fetch(`${API_BASE_URL}/api/families/${authData.family.id}/children`);
                const result = await response.json();
                if (result.success) {
                  setChildren(result.data || []);
                } else {
                  setChildren(authData.children || []);
                }
              } catch (error) {
                setChildren(authData.children || []);
              }
            } else {
              setChildren(authData.children || []);
            }

            navigate('/dashboard', { replace: true });
          }
        } catch (error) {
          console.error('Error parsing saved auth data:', error);
          localStorage.removeItem('authData');
          localStorage.removeItem('userType');
          navigate('/', { replace: true });
        }
      } else {
        console.log('App: No existing auth found');
        // Only navigate to welcome if not on a valid public route
        const publicRoutes = ['/', '/login', '/kid-login', '/create-family', '/join-family', '/onboarding'];
        if (!publicRoutes.includes(location.pathname)) {
          navigate('/', { replace: true });
        }
      }
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Save auth state
  useEffect(() => {
    if (currentUser && userType && !isLoggingOut) {
      const authData = {
        user: currentUser,
        family: currentFamily,
        guardian: currentGuardian,
        child: currentChild,
        children: children,
        guardians: guardians
      };
      try {
        localStorage.setItem('authData', JSON.stringify(authData));
        localStorage.setItem('userType', userType);
      } catch (error) {
        console.error('Error saving auth state:', error);
      }
    } else if (isLoggingOut) {
      localStorage.removeItem('authData');
      localStorage.removeItem('userType');
    }
  }, [currentUser, currentFamily, currentGuardian, currentChild, children, guardians, userType, isLoggingOut]);

  const loadChildrenData = async (familyId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/families/${familyId}/children`);
      const result = await response.json();
      if (result.success) {
        setChildren(result.data || []);
      }
    } catch (error) {
      console.error('App: Error loading children:', error);
    }
  };

  // Navigation handler - now uses React Router
  const handleNavigate = (screen, data = null, options = {}) => {
    console.log('App.js: Navigating to:', screen, 'with data:', data);

    if (screen === 'childDashboard' && data && options.refreshParent) {
      setParentRefreshCallback(() => options.refreshParent);
    }

    if (screen === 'familyDashboard') {
      setCurrentChild(null);
      if (parentRefreshCallback) {
        loadChildrenData(currentFamily?.id);
        setParentRefreshCallback(null);
      }
    }

    if (screen === 'childDashboard' && data) {
      setCurrentChild(data);
    }

    if (screen === 'addMoney' && data) {
      setCurrentChild(data);
    }

    // Map screen names to routes
    const screenToRoute = {
      welcome: '/',
      createFamily: '/create-family',
      joinExistingFamily: '/join-family',
      guardianLogin: '/login',
      kidLogin: '/kid-login',
      familyDashboard: '/dashboard',
      childDashboard: '/child-dashboard',
      addChild: '/add-child',
      addMoney: '/add-money',
      ledgerBook: '/ledger',
      financialLiteracyProgram: '/learn',
      adminDashboard: '/admin',
      successScreen: '/success',
      onboardingFamily: '/onboarding'
    };

    // Handle financial literacy with params
    if (screen === 'financialLiteracyChapter' && data?.chapterId) {
      navigate(`/learn/chapter/${data.chapterId}`);
      return;
    }

    if (screen === 'financialLiteracyLesson' && data?.chapterId && data?.lessonId) {
      navigate(`/learn/chapter/${data.chapterId}/lesson/${data.lessonId}`);
      return;
    }

    const route = screenToRoute[screen] || '/';
    navigate(route);
  };

  // Handle successful family join
  const handleJoinSuccess = (joinData) => {
    console.log('App: Family joined successfully:', joinData);
    localStorage.setItem('authData', JSON.stringify(joinData));
    localStorage.setItem('userType', joinData.accountType);

    setCurrentFamily(joinData.family);
    setUserType(joinData.accountType);

    if (joinData.accountType === 'child') {
      setCurrentChild(joinData.child);
      setCurrentUser(joinData.user);
      navigate('/child-dashboard');
    } else {
      setCurrentGuardian(joinData.guardian);
      setCurrentUser(joinData.user);
      if (joinData.children) {
        setChildren(joinData.children);
      }
      navigate('/dashboard');
    }
  };

  const handleLogin = (authData) => {
    console.log('App: Login successful, processing auth data...', authData);
    localStorage.setItem('authData', JSON.stringify(authData));
    localStorage.setItem('userType', authData.user?.type);

    setUserType(authData.user?.type);

    if (authData.user.type === 'admin') {
      setCurrentUser(authData.user);
      navigate('/admin');
      return;
    }

    if (authData.user.type === 'child') {
      setCurrentUser(authData.user);
      setCurrentChild(authData.child);
      setCurrentFamily(authData.family);
      navigate('/child-dashboard');
      return;
    }

    // Guardian login
    if (authData.children !== undefined && authData.guardians !== undefined) {
      setCurrentUser(authData.user);
      setCurrentGuardian(authData.guardian);
      setCurrentFamily(authData.family);
      setChildren(authData.children);
      setGuardians(authData.guardians);
      navigate('/dashboard');
    } else {
      setLoginData(authData);
      navigate('/login-handler');
    }
  };

  const handleLoginComplete = (data) => {
    setCurrentUser(data.user);
    setCurrentGuardian(data.guardian);
    setCurrentFamily(data.family);
    setChildren(data.children);
    setGuardians(data.guardians);

    const completeAuthData = {
      user: data.user,
      guardian: data.guardian,
      family: data.family,
      children: data.children,
      guardians: data.guardians
    };
    localStorage.setItem('authData', JSON.stringify(completeAuthData));
    localStorage.setItem('userType', data.user?.type);

    navigate('/dashboard');
  };

  const handleLoginError = (error) => {
    console.error('App: LoginHandler error:', error);
    navigate('/');
  };

  const handleFamilyCreated = (data) => {
    console.log('App: Family created successfully', data);
    const authData = { user: data.user, guardian: data.guardian, family: data.family };
    localStorage.setItem('authData', JSON.stringify(authData));
    localStorage.setItem('userType', 'guardian');

    setCurrentUser(data.user);
    setCurrentGuardian(data.guardian);
    setCurrentFamily(data.family);
    setUserType('guardian');
    navigate('/success');
  };

  const handleChildCreated = async (newChild) => {
    console.log('App: Child created successfully:', newChild);
    setChildren(prevChildren => [...prevChildren, newChild]);
    setCurrentChild(newChild);
    navigate('/child-dashboard');
  };

  const handleLogout = async () => {
    console.log('App: Starting logout process...');
    setIsLoggingOut(true);
    localStorage.removeItem('authData');
    localStorage.removeItem('userType');

    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error during logout:', error);
    }

    setCurrentUser(null);
    setUserType(null);
    setCurrentGuardian(null);
    setCurrentFamily(null);
    setChildren([]);
    setGuardians([]);
    setCurrentChild(null);
    setInviteCode('');

    navigate('/');

    setTimeout(() => {
      setIsLoggingOut(false);
    }, 500);
  };

  // Loading screen
  if (isAuthLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8fafc',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e2e8f0',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '16px', color: '#64748b', fontSize: '16px' }}>Loading...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<WelcomeScreen onNavigate={handleNavigate} />} />

      <Route path="/create-family" element={
        <FamilyCreation onNavigate={handleNavigate} onFamilyCreated={handleFamilyCreated} />
      } />

      <Route path="/join-family" element={
        <JoinExistingFamily
          onNavigate={handleNavigate}
          onJoinSuccess={handleJoinSuccess}
          inviteCodeParam={inviteCode}
        />
      } />

      <Route path="/invite/:code" element={
        <JoinExistingFamily
          onNavigate={handleNavigate}
          onJoinSuccess={handleJoinSuccess}
          inviteCodeParam={inviteCode}
        />
      } />

      <Route path="/login" element={
        <GuardianLogin onNavigate={handleNavigate} onLogin={handleLogin} />
      } />

      <Route path="/kid-login" element={
        <KidLogin onNavigate={handleNavigate} onLogin={handleLogin} />
      } />

      <Route path="/onboarding" element={
        <OnboardingFamilyCreation onNavigate={handleNavigate} onFamilyCreated={handleFamilyCreated} />
      } />

      <Route path="/login-handler" element={
        <LoginHandler loginData={loginData} onComplete={handleLoginComplete} onError={handleLoginError} />
      } />

      <Route path="/success" element={
        <FamilyCreationSuccess
          family={currentFamily}
          guardian={currentGuardian}
          user={currentUser}
          onNavigate={handleNavigate}
        />
      } />

      {/* Protected Routes - Dashboard */}
      <Route path="/dashboard" element={
        <FamilyDashboard
          family={currentFamily}
          guardian={currentGuardian}
          children={children}
          guardians={guardians}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
        />
      } />

      <Route path="/admin" element={
        <AdminDashboard currentUser={currentUser} onLogout={handleLogout} />
      } />

      <Route path="/child-dashboard" element={
        <ChildDashboard
          child={currentChild}
          family={currentFamily}
          guardian={currentGuardian}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
          userType={userType}
          isInitialLoad={isAuthLoading}
          onAddMoney={(child) => {
            setCurrentChild(child);
            navigate('/add-money');
          }}
          onSetGoal={(child) => console.log('Set goal for child:', child)}
        />
      } />

      <Route path="/add-child" element={
        <AddChild
          family={currentFamily}
          guardian={currentGuardian}
          onNavigate={handleNavigate}
          onChildCreated={handleChildCreated}
        />
      } />

      <Route path="/add-money" element={
        <AddMoney
          child={currentChild}
          family={currentFamily}
          guardian={currentGuardian}
          onNavigate={handleNavigate}
          userType={userType}
          onDepositRequested={(depositData) => console.log('App: Deposit requested:', depositData)}
        />
      } />

      <Route path="/ledger" element={
        <LedgerBookPage family={currentFamily} children={children} onNavigate={handleNavigate} />
      } />

      {/* Financial Literacy Routes */}
      <Route path="/learn" element={
        <FinancialLiteracyProgram child={currentChild} family={currentFamily} onNavigate={handleNavigate} />
      } />

      <Route path="/learn/chapter/:chapterId" element={
        <ChapterWrapper currentChild={currentChild} currentFamily={currentFamily} onNavigate={handleNavigate} />
      } />

      <Route path="/learn/chapter/:chapterId/lesson/:lessonId" element={
        <LessonWrapper currentChild={currentChild} currentFamily={currentFamily} onNavigate={handleNavigate} />
      } />

      {/* Fallback - redirect to home */}
      <Route path="*" element={<WelcomeScreen onNavigate={handleNavigate} />} />
    </Routes>
  );
};

// Wrapper components for routes with params
const ChapterWrapper = ({ currentChild, currentFamily, onNavigate }) => {
  const { chapterId } = useParams();
  return (
    <Chapter
      chapterId={chapterId}
      child={currentChild}
      family={currentFamily}
      onNavigate={onNavigate}
    />
  );
};

const LessonWrapper = ({ currentChild, currentFamily, onNavigate }) => {
  const { chapterId, lessonId } = useParams();
  return (
    <FinancialLiteracyLessonRenderer
      chapterId={chapterId}
      lessonId={lessonId}
      child={currentChild}
      family={currentFamily}
      onNavigate={onNavigate}
    />
  );
};

export default App;
